import { Settings } from "../../settings";

/**
 * turboRest — the turbo landing's rest state, made SURE of (2026-10-29).
 *
 * A turbo spin that is not held (and has no win or trigger) rests every
 * symbol on its spine — its `idle` when it has one, else its `static`
 * (useLandingAnimations / useFreeSpinBasicLandingAnimations). The landing
 * hook decides that per REEL as each reel lands, reading the hold flag at that
 * moment, so two cases slipped through (he: "sometimes it fails … has to do
 * with the hold duration"):
 *
 *   let go mid-landing   reels that landed while held kept their pictures,
 *                        the rest rested — a mixed board
 *   let go after it      the held run's last board landed as pictures and
 *                        nothing looked at it again — all pictures
 *
 * This puts every LANDED cell still showing its picture onto that rest. Cells
 * already on a spine (idle, static, a landing or anticipation clip) are left
 * alone, and a reel still spinning is never touched — so it can run any time,
 * any number of times.
 *
 * @param {Object}   p
 * @param {Array}    p.overlayControllers  one per reel
 * @param {Array}    p.reelControllers     one per reel
 * @param {number}  [p.skipReel]           a reel the caller handles itself
 * @param {Function}[p.prepareCell]        (overlay, reel, row, symbolId) before
 *                                         the spine starts (z-order, skin)
 * @returns {number} cells put to rest
 */
export const restLandedPictures = ({
  overlayControllers,
  reelControllers,
  skipReel = -1,
  prepareCell = null,
}) => {
  const hides = [];
  for (let reel = 0; reel < Settings.REEL_COUNT; reel++) {
    if (reel === skipReel) continue;
    const overlay = overlayControllers?.[reel];
    const reelCtrl = reelControllers?.[reel];
    if (!overlay || !reelCtrl) continue;
    // Landed only — never a reel still spinning or stopping.
    if (reelCtrl.hasStopped !== true || reelCtrl.isStopping) continue;

    for (let row = 0; row < Settings.SYMBOLS_VISIBLE; row++) {
      const symbol = reelCtrl.symbolsContainer?.children?.[row];
      const symbolId = symbol?.currentSymbolId ?? symbol?.symbolId;
      // A settled picture (a gamble window, a composite) stays a picture.
      if (symbolId === undefined || symbol?.hasStaticOverride) continue;

      const entry = overlay.activeAnimations?.get(row);
      const spine = entry?.spineInstance;
      if (
        entry &&
        entry.symbolId === symbolId &&
        spine &&
        spine.visible !== false &&
        spine.alpha >= 0.5
      ) {
        continue; // already on its spine
      }

      const clip = overlay.hasClip?.(symbolId, "idle")
        ? "idle"
        : overlay.hasClip?.(symbolId, "static")
          ? "static"
          : null;
      if (!clip) continue;

      prepareCell?.(overlay, reel, row, symbolId);
      overlay.showIdleAnimation(row, symbolId, null, clip === "static", clip);
      hides.push([reelCtrl, row]);
    }
  }
  // The picture goes a frame later, as the landing hook does it — the spine
  // has drawn by then, so nothing flashes empty.
  if (hides.length) {
    requestAnimationFrame(() =>
      hides.forEach(([reelCtrl, row]) => reelCtrl.hideSymbolTexture?.(row)),
    );
  }
  return hides.length;
};
